/**
 * Offline Service Module
 * Manages product caching for offline use with Vite PWA / Workbox
 */

import type { ProductConfig, ColorVariant } from '../types';

// Default renk (hiç renk yoksa kullanılır)
const DEFAULT_COLOR: ColorVariant = { id: 'default', name: 'Standart', color: '#6B7280' };

export interface CacheProgress {
  productId: string;
  loaded: number;
  failed: number;
  total: number;
}

export interface CacheResult {
  productId: string;
  success: boolean;
  loaded: number;
  failed: number;
  total: number;
}

type ProgressCallback = (progress: CacheProgress) => void;
type CompleteCallback = (result: CacheResult) => void;
type ErrorCallback = (error: { productId: string; error: string }) => void;

const PRODUCT_DATA_CACHE = 'product-data';
const PRODUCT_IMAGES_CACHE = 'product-images';

class OfflineServiceClass {
  private cachedProductsCache: Set<string> = new Set();

  /**
   * Check if Service Worker is supported
   */
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'caches' in window;
  }

  /**
   * Initialize and check cached products
   */
  async register(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Request persistent storage (prevents browser from auto-clearing cache)
      await this.requestPersistentStorage();

      // Load cached products list
      await this.refreshCachedProductsList();

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Request persistent storage for higher quota and prevent auto-eviction
   */
  private async requestPersistentStorage(): Promise<void> {
    if (navigator.storage && navigator.storage.persist) {
      const isPersisted = await navigator.storage.persisted();
      if (!isPersisted) {
        const granted = await navigator.storage.persist();
        if (granted) {
        }
      }
    }
  }

  /**
   * Get all image URLs for a product
   * Returns both fetch URL (for downloading via proxy) and cache URL (for storing)
   * Proxy CORS sorununu çözer, cache'e gerçek URL ile yazılır
   */
  private getProductImageUrls(config: ProductConfig): { fetchUrl: string; cacheUrl: string }[] {
    const imageBaseURL = import.meta.env.VITE_IMAGE_BASE_URL;
    const proxyBaseURL = `${imageBaseURL}/proxy.php?f=`;
    const colors = config.colorVariants && config.colorVariants.length > 0
      ? config.colorVariants
      : [DEFAULT_COLOR];

    const urls: { fetchUrl: string; cacheUrl: string }[] = [];
    const filePrefix = config.images.basePath.split('/').pop() || config.images.basePath;

    for (const color of colors) {
      // Her renk için override'ları kontrol et
      const override = config.variantOverrides?.[color.id];
      const count = override?.count ?? config.images.count;
      const extension = override?.extension ?? config.images.extension;

      for (let i = 1; i <= count; i++) {
        // Görsel yolu (proxy için ve cache key için)
        const imagePath = `${config.images.basePath}/${color.id}/${filePrefix}-${i}.${extension}`;

        // Gerçek URL (cache'de bu URL ile saklanacak, ImageLoader bu URL'yi kullanıyor)
        const cacheUrl = `${imageBaseURL}/${imagePath}`;

        // Fetch URL (proxy üzerinden - CORS sorunu yok)
        const fetchUrl = `${proxyBaseURL}${imagePath}`;

        urls.push({ fetchUrl, cacheUrl });
      }
    }

    return urls;
  }

  /**
   * Cache a product for offline use
   * Doğrudan Cache API kullanarak görselleri kaydeder (Workbox'a güvenmez)
   */
  async cacheProduct(
    productId: string,
    productData: ProductConfig,
    onProgress?: ProgressCallback,
    onComplete?: CompleteCallback,
    onError?: ErrorCallback
  ): Promise<void> {
    try {
      // 1. Cache product data
      const dataCache = await caches.open(PRODUCT_DATA_CACHE);
      const dataResponse = new Response(JSON.stringify(productData), {
        headers: { 'Content-Type': 'application/json' }
      });
      await dataCache.put(`/offline-product/${productId}`, dataResponse);

      // 2. Görselleri fetch edip doğrudan Cache API'ye yaz
      const imageCache = await caches.open(PRODUCT_IMAGES_CACHE);
      const imageUrls = this.getProductImageUrls(productData);
      const total = imageUrls.length;
      let loaded = 0;
      let failed = 0;

      // Load images with concurrency limit (4 at a time)
      const concurrency = 4;
      for (let i = 0; i < imageUrls.length; i += concurrency) {
        const batch = imageUrls.slice(i, i + concurrency);

        await Promise.all(
          batch.map(async ({ fetchUrl, cacheUrl }) => {
            try {
              // fetchUrl ile indir (dev'de proxy, prod'da gerçek URL)
              const response = await fetch(fetchUrl);

              if (response.ok) {
                // cacheUrl ile cache'e yaz (ImageLoader bu URL'yi kullanır)
                await imageCache.put(cacheUrl, response.clone());
                loaded++;
              } else {
                console.warn(`[OfflineService] Failed to fetch: ${fetchUrl} (${response.status})`);
                failed++;
              }
            } catch (err) {
              console.warn(`[OfflineService] Network error for: ${fetchUrl}`, err);
              failed++;
            }

            onProgress?.({ productId, loaded, failed, total });
          })
        );
      }

      // Mark as cached
      this.cachedProductsCache.add(productId);

      onComplete?.({
        productId,
        success: failed === 0,
        loaded,
        failed,
        total
      });

    } catch (error) {
      onError?.({
        productId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  /**
   * Delete a product's cache
   */
  async deleteProductCache(productId: string): Promise<void> {
    try {
      // Delete product data
      const dataCache = await caches.open(PRODUCT_DATA_CACHE);
      await dataCache.delete(`/offline-product/${productId}`);

      // Note: Image cache is shared across products (workbox manages it)
      // We don't delete individual images as they might be used by other products

      this.cachedProductsCache.delete(productId);
    } catch {
      // Ignore errors
    }
  }

  /**
   * Refresh the list of cached products
   */
  async refreshCachedProductsList(): Promise<void> {
    try {
      const dataCache = await caches.open(PRODUCT_DATA_CACHE);
      const keys = await dataCache.keys();

      this.cachedProductsCache = new Set(
        keys
          .map(req => {
            const match = req.url.match(/\/offline-product\/(.+)$/);
            return match ? match[1] : null;
          })
          .filter((id): id is string => id !== null)
      );
    } catch {
      this.cachedProductsCache = new Set();
    }
  }

  /**
   * Check if a product is cached
   */
  isProductCached(productId: string): boolean {
    return this.cachedProductsCache.has(productId);
  }

  /**
   * Get all cached product IDs
   */
  getCachedProductIds(): string[] {
    return Array.from(this.cachedProductsCache);
  }

  /**
   * Calculate total images for a product
   * Fotoğraflar sadece renge bağlı, custom varyant sayısı etkilemiyor
   * variantOverrides ile her renk için farklı count olabilir
   */
  calculateTotalImages(config: ProductConfig): number {
    const colors = config.colorVariants && config.colorVariants.length > 0
      ? config.colorVariants
      : [DEFAULT_COLOR];

    let total = 0;
    for (const color of colors) {
      const override = config.variantOverrides?.[color.id];
      const count = override?.count ?? config.images.count;
      total += count;
    }
    return total;
  }
}

// Singleton instance
export const OfflineService = new OfflineServiceClass();
